import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { repository } from '@/repositories';
import { Badge, Bar, Icon, Ring, MobileHeader, EmptyState, useToast } from '@/components';
import { useCourse, useEnrollments, useTestQuestions, useSubmitTest } from '@/hooks/queries';
import { buildShuffledView, makeSeed, toOriginalAnswer } from '@/lib/shuffle';
import type { QuizAnswers, TestResult } from '@/types/domain';
import { QuizQuestionView } from './QuizQuestionView';

type Stage = 'intro' | 'quiz' | 'result';
type Answers = QuizAnswers;
type TestDraft = { i: number; answers: Answers; seed: number; updatedAt: number };

const DRAFT_PREFIX = 'wp:test-draft:v1';

/** Live m:ss remaining until an ISO timestamp; active=false once it elapses. */
function useCooldown(untilIso?: string | null): { active: boolean; label: string } {
  const until = untilIso ? Date.parse(untilIso) : 0;
  const [now, setNow] = useState(() => Date.now());
  const active = Number.isFinite(until) && until > now;
  useEffect(() => {
    if (!active) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [active]);
  const ms = Math.max(0, until - now);
  return { active, label: `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}` };
}

function isAnswer(value: unknown): value is number | number[] {
  return typeof value === 'number' || (Array.isArray(value) && value.every((x) => typeof x === 'number'));
}

function normalizeDraft(value: unknown): TestDraft | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Partial<TestDraft>;
  const rawAnswers = raw.answers && typeof raw.answers === 'object' ? raw.answers : {};
  const answers = Object.fromEntries(
    Object.entries(rawAnswers).filter((entry): entry is [string, number | number[]] => isAnswer(entry[1])),
  );
  return {
    i: typeof raw.i === 'number' && Number.isFinite(raw.i) ? Math.max(0, Math.floor(raw.i)) : 0,
    seed: typeof raw.seed === 'number' && Number.isFinite(raw.seed) ? raw.seed : makeSeed(),
    answers,
    updatedAt: typeof raw.updatedAt === 'number' && Number.isFinite(raw.updatedAt) ? raw.updatedAt : Date.now(),
  };
}

function loadDraft(key: string): TestDraft | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? normalizeDraft(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function saveDraft(key: string, draft: Omit<TestDraft, 'updatedAt'>) {
  try {
    window.localStorage.setItem(key, JSON.stringify({ ...draft, updatedAt: Date.now() }));
  } catch {
    // Local storage can be disabled; losing resume is better than blocking the quiz.
  }
}

function clearDraft(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage failures.
  }
}

function toDisplayAnswer(optionMap: number[], answer: number | number[] | undefined): number | number[] | undefined {
  if (answer === undefined) return undefined;
  const findDisplayIndex = (original: number) => optionMap.findIndex((x) => x === original);
  if (Array.isArray(answer)) {
    const mapped = answer.map(findDisplayIndex).filter((x) => x >= 0);
    return mapped.length ? mapped : undefined;
  }
  const mapped = findDisplayIndex(answer);
  return mapped >= 0 ? mapped : undefined;
}

export function LearnerTest() {
  const { id = '', mode = 'post' } = useParams();
  const navigate = useNavigate();
  const isPre = mode === 'pre';
  const testKind = isPre ? 'pre' : 'post';
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: course } = useCourse(id);
  const { data: enrollments = [] } = useEnrollments();
  const { data: questions = [] } = useTestQuestions(id, testKind); // answer-safe — server grades
  const submitTest = useSubmitTest();
  const [stage, setStage] = useState<Stage>('intro');
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [result, setResult] = useState<TestResult | null>(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  // Per-attempt shuffle: stable within a sitting (fixed seed), re-rolled on retake.
  const [seed, setSeed] = useState(makeSeed);
  const serverSaveTimer = useRef<number | undefined>(undefined);
  const draftKey = useMemo(() => `${DRAFT_PREFIX}:${user?.id ?? 'anon'}:${id}:${testKind}`, [user?.id, id, testKind]);
  const view = useMemo(
    () =>
      buildShuffledView(
        questions,
        { shuffleQuestions: course?.shuffleQuestions, shuffleChoices: course?.shuffleChoices },
        seed,
      ),
    [questions, course?.shuffleQuestions, course?.shuffleChoices, seed],
  );
  // Post-test: after 3 attempts the server gates the next try for 5 minutes.
  const cooldown = useCooldown(result?.cooldownUntil);

  useEffect(() => {
    setDraftLoaded(false);
    setStage('intro');
    setI(0);
    setAnswers({});
    setResult(null);
    setSeed(makeSeed());

    const draft = loadDraft(draftKey);
    if (draft) {
      setI(draft.i);
      setAnswers(draft.answers);
      setSeed(draft.seed);
      setHasDraft(true);
    } else {
      setHasDraft(false);
    }
    setDraftLoaded(true);

    // Server draft is authoritative — it survives the device storage being wiped
    // (LINE in-app browser), re-login, and switching devices. Apply it if it has
    // answers, overriding any local draft.
    let cancelled = false;
    void repository
      .getTestDraft(id, testKind)
      .then((d) => {
        if (cancelled || !d || Object.keys(d.answers).length === 0) return;
        setI(d.i);
        setAnswers(d.answers);
        if (d.seed != null) setSeed(d.seed);
        setHasDraft(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [draftKey, id, testKind]);

  useEffect(() => {
    if (view.length > 0 && i >= view.length) setI(view.length - 1);
  }, [i, view.length]);

  useEffect(() => {
    if (!draftLoaded || stage !== 'quiz') return;
    saveDraft(draftKey, { i, answers, seed }); // immediate local cache
    setHasDraft(true);
    // Debounced server save — the durable copy.
    window.clearTimeout(serverSaveTimer.current);
    serverSaveTimer.current = window.setTimeout(() => {
      void repository.saveTestDraft(id, testKind, { answers, i, seed }).catch(() => {});
    }, 700);
  }, [answers, draftKey, draftLoaded, i, seed, stage, id, testKind]);

  if (!course) return <EmptyState icon="doc" title="Test not found" />;

  // The post-test is gated behind attendance — you must check in first.
  const enr = enrollments.find((e) => e.courseId === id);
  if (!isPre && enr && enr.attended !== true) {
    return (
      <div style={{ background: 'var(--paper)', minHeight: '100%' }}>
        <MobileHeader title="Post-test" th="แบบทดสอบหลังเรียน" onBack={() => navigate(-1)} large={false} />
        <div style={{ maxWidth: 480, margin: '0 auto', padding: '32px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center', minHeight: '60vh', justifyContent: 'center' }}>
          <div style={{ width: 72, height: 72, borderRadius: 18, background: 'var(--ochre-soft)', display: 'grid', placeItems: 'center', color: 'var(--ochre)' }}>
            <Icon name="lock" size={32} />
          </div>
          <h2 style={{ fontSize: 21, fontWeight: 600, margin: 0 }}>Check in first</h2>
          <p className="th" style={{ fontSize: 14.5, color: 'var(--ink-2)', lineHeight: 1.55, margin: 0, maxWidth: 320 }}>
            กรุณาเช็คอินเข้าร่วมการอบรมก่อน จึงจะทำแบบทดสอบหลังเรียนได้ · The post-test unlocks after you check in at the seminar.
          </p>
          <button className="btn btn-primary btn-lg btn-block" style={{ maxWidth: 280 }} onClick={() => navigate(`/learn/course/${id}/attendance`)}>
            <Icon name="qr" size={17} /> Go to check-in
          </button>
        </div>
      </div>
    );
  }

  // Pre-test is a one-time baseline — once taken, don't allow a retake.
  // (Skip while a fresh result is on screen so the learner can read their score.)
  if (isPre && enr && enr.pretest != null && stage !== 'result') {
    return (
      <div style={{ background: 'var(--paper)', minHeight: '100%' }}>
        <MobileHeader title="Pre-test" th="แบบทดสอบก่อนเรียน" onBack={() => navigate(-1)} large={false} />
        <div style={{ maxWidth: 480, margin: '0 auto', padding: '32px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center', minHeight: '60vh', justifyContent: 'center' }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--sage-soft)', display: 'grid', placeItems: 'center', color: 'var(--sage)' }}>
            <Icon name="check" size={40} stroke={2.2} />
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Pre-test complete</h2>
          <p className="th" style={{ fontSize: 14.5, color: 'var(--ink-2)', lineHeight: 1.55, margin: 0, maxWidth: 320 }}>
            คุณทำแบบทดสอบก่อนเรียนแล้ว ({enr.pretest}%) · You've already taken the pre-test. Continue the course.
          </p>
          <button className="btn btn-primary btn-lg btn-block" style={{ maxWidth: 280 }} onClick={() => navigate(`/learn/catalog/${id}`)}>Continue <Icon name="arrowR" size={17} /></button>
        </div>
      </div>
    );
  }

  // Post-test is a one-time pass — once cleared, don't allow a retake.
  // (Skip while a fresh result is on screen so the learner can read their score.)
  if (!isPre && enr && enr.posttest != null && enr.posttest >= course.passScore && stage !== 'result') {
    return (
      <div style={{ background: 'var(--paper)', minHeight: '100%' }}>
        <MobileHeader title="Post-test" th="แบบทดสอบหลังเรียน" onBack={() => navigate(-1)} large={false} />
        <div style={{ maxWidth: 480, margin: '0 auto', padding: '32px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center', minHeight: '60vh', justifyContent: 'center' }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--sage-soft)', display: 'grid', placeItems: 'center', color: 'var(--sage)' }}>
            <Icon name="check" size={40} stroke={2.2} />
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Post-test passed</h2>
          <p className="th" style={{ fontSize: 14.5, color: 'var(--ink-2)', lineHeight: 1.55, margin: 0, maxWidth: 320 }}>
            คุณทำแบบทดสอบหลังเรียนผ่านแล้ว ({enr.posttest}%) · You already passed ({enr.posttest}%). This requirement is complete.
          </p>
          {enr.eval ? (
            <button className="btn btn-primary btn-lg btn-block" style={{ maxWidth: 280 }} onClick={() => navigate('/learn/wallet')}><Icon name="award" size={17} /> View certificate</button>
          ) : (
            <button className="btn btn-primary btn-lg btn-block" style={{ maxWidth: 280 }} onClick={() => navigate(`/learn/course/${id}/eval`)}><Icon name="star" size={17} /> Continue to survey</button>
          )}
        </div>
      </div>
    );
  }

  const pick = (qid: string, opt: number, multi: boolean) =>
    setAnswers((prev) => {
      if (!multi) return { ...prev, [qid]: opt };
      const cur = (prev[qid] as number[]) || [];
      return { ...prev, [qid]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
    });

  const submit = async () => {
    // Map each pick from its shuffled DISPLAY index back to the ORIGINAL option
    // index, so server grading (original order) is unaffected by the shuffle.
    const original: Answers = {};
    for (const q of view) {
      const a = answers[q.id];
      if (a != null) original[q.id] = toOriginalAnswer(q.optionMap, a);
    }
    try {
      const res = await submitTest.mutateAsync({ courseId: id, kind: testKind, answers: original });
      clearDraft(draftKey);
      void repository.clearTestDraft(id, testKind).catch(() => {});
      setHasDraft(false);
      setResult(res);
      setStage('result');
    } catch (err) {
      const msg = (err as { message?: string } | null)?.message ?? String(err);
      toast(msg.includes('cooldown') ? 'Please wait a few minutes before retrying the post-test.' : 'Could not submit — please try again.', { tone: 'clay', icon: 'clock' });
    }
  };

  if (stage === 'intro') {
    const rows: [string, string][] = [
      ['Questions', `${view.length}`],
      ['Passing score', `${course.passScore}%`],
      ['Attempts', isPre ? 'Unlimited' : 'Unlimited · 5-min wait after every 3'],
    ];
    return (
      <div style={{ background: 'var(--paper)', minHeight: '100%' }}>
        <MobileHeader title={isPre ? 'Pre-test' : 'Post-test'} th={isPre ? 'แบบทดสอบก่อนเรียน' : 'แบบทดสอบหลังเรียน'} onBack={() => navigate(-1)} large={false} />
        <div className="hidden md:block" style={{ padding: '18px 28px 0' }}>
          <button onClick={() => navigate(-1)} className="btn btn-ghost btn-sm"><Icon name="chevL" size={15} /> Back</button>
        </div>
        <div style={{ maxWidth: 560, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20, padding: '18px 20px', minHeight: '60vh' }}>
          <div className="card" style={{ padding: '28px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 64, height: 64, borderRadius: 18, background: 'var(--sage-soft)', display: 'grid', placeItems: 'center', color: 'var(--sage-deep)', marginBottom: 6 }}>
              <Icon name="doc" size={28} />
            </div>
            <div className="th" style={{ fontSize: 17, fontWeight: 600 }}>{course.title.th}</div>
            <div style={{ fontSize: 13.5, color: 'var(--ink-3)' }}>{course.title.en}</div>
          </div>
          <div className="card divide">
            {rows.map(([k, v], n) => (
              <div key={n} style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 16px' }}>
                <span style={{ fontSize: 14, color: 'var(--ink-3)' }}>{k}</span>
                <span style={{ fontSize: 14.5, fontWeight: 500 }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, fontSize: 13.5, color: 'var(--ink-2)', padding: '0 4px' }}>
            <Icon name="shield" size={16} style={{ color: 'var(--sage)', flex: 'none', marginTop: 1 }} /> Passing this test satisfies one of your certificate requirements.
          </div>
          {hasDraft && (
            <div style={{ display: 'flex', gap: 8, fontSize: 13.5, color: 'var(--ink-2)', padding: '0 4px' }}>
              <Icon name="clock" size={16} style={{ color: 'var(--sage)', flex: 'none', marginTop: 1 }} /> Saved progress found. You can continue where you left off.
            </div>
          )}
          <button className="btn btn-primary btn-lg btn-block" disabled={!view.length} onClick={() => setStage('quiz')}>{hasDraft ? 'Resume test' : 'Begin test'}</button>
        </div>
      </div>
    );
  }

  if (stage === 'result' && result) {
    const sc = result.score;
    const passed = result.passed;
    const cooling = !passed && !isPre && cooldown.active; // 5-min wait after 3 post attempts
    const showAnswerReview = result.reveal === true && result.key != null;
    return (
      <div style={{ background: 'var(--paper)', minHeight: '100%', display: 'flex', alignItems: showAnswerReview ? 'flex-start' : 'center', justifyContent: 'center' }}>
        <div style={{ padding: 32, textAlign: 'center', width: '100%', maxWidth: showAnswerReview ? 760 : 420 }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Ring value={sc} size={132} stroke={9} color={passed ? 'var(--sage)' : 'var(--clay)'} label={`${sc}`} />
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 600, margin: '24px 0 6px' }}>{isPre ? 'Pre-test complete' : passed ? 'You passed' : 'Not quite yet'}</h2>
          <div className="th" style={{ color: 'var(--ink-3)', fontSize: 14 }}>{isPre ? 'ทำแบบทดสอบก่อนเรียนเสร็จแล้ว' : passed ? 'ผ่านเกณฑ์' : 'ยังไม่ผ่านเกณฑ์'}</div>
          <p style={{ color: 'var(--ink-2)', fontSize: 15, lineHeight: 1.55, margin: '16px auto 0', maxWidth: 340 }}>
            {isPre
              ? 'Your baseline is recorded. The lessons are now unlocked — continue the course.'
              : passed
              ? `You scored above the ${result.passScore}% threshold. This requirement is now complete.`
              : cooling
                ? `You need ${result.passScore}% to pass. You've used 3 attempts — you can retry in ${cooldown.label}.`
                : `You need ${result.passScore}% to pass. Review the lessons and try again.`}
          </p>
          {showAnswerReview && (
            <div style={{ marginTop: 28, textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <h3 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>Answer review</h3>
                <Badge kind="sage">{isPre ? 'Pre-test review' : 'Post-test review'}</Badge>
              </div>
              {view.map((question, n) => (
                <div key={question.id} className="card" style={{ padding: 18 }}>
                  <div className="u-mono" style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>Question {n + 1}</div>
                  <QuizQuestionView
                    q={question}
                    selected={answers[question.id]}
                    correct={toDisplayAnswer(question.optionMap, result.key?.[question.id])}
                    onPick={() => undefined}
                  />
                  {result.rationale?.[question.id] && (
                    <div className="th" style={{ marginTop: 12, padding: '10px 12px', background: 'var(--sage-tint)', borderRadius: 9, fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>
                      {result.rationale[question.id]}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 30, maxWidth: 320, marginInline: 'auto' }}>
            {isPre ? (
              <button className="btn btn-primary btn-lg btn-block" onClick={() => navigate(`/learn/catalog/${id}`)}>Continue <Icon name="arrowR" size={17} /></button>
            ) : passed ? (
              enr?.eval === true ? (
                <button className="btn btn-primary btn-lg btn-block" onClick={() => navigate('/learn/wallet')}><Icon name="award" size={17} /> View certificate</button>
              ) : (
                <button className="btn btn-primary btn-lg btn-block" onClick={() => navigate(`/learn/course/${id}/eval`)}><Icon name="star" size={17} /> Continue to survey</button>
              )
            ) : (
              <button
                className="btn btn-primary btn-lg btn-block"
                disabled={cooling}
                onClick={() => { clearDraft(draftKey); setHasDraft(false); setStage('quiz'); setI(0); setAnswers({}); setResult(null); setSeed(makeSeed()); }}
              >
                {cooling ? `Retry in ${cooldown.label}` : 'Retake test'}
              </button>
            )}
            {!isPre && <button className="btn btn-quiet btn-block" onClick={() => navigate(`/learn/catalog/${id}`)}>{passed ? 'Back to course' : 'Review lessons'}</button>}
          </div>
        </div>
      </div>
    );
  }

  // quiz
  const q = view[i];
  if (!q) return <EmptyState icon="doc" title="No questions yet" />;
  const multi = q.type === 'multiple';
  const a = answers[q.id];
  const answered = multi ? Array.isArray(a) && a.length > 0 : a != null;
  const last = i === view.length - 1;

  return (
    <div style={{ background: 'var(--paper)', minHeight: '100%' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 30, background: 'var(--paper)', paddingTop: 'calc(env(safe-area-inset-top) + 14px)', borderBottom: '1px solid var(--line)' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '6px 20px 12px', maxWidth: 640, margin: '0 auto' }}>
          <button onClick={() => (i === 0 ? navigate(-1) : setI(i - 1))} style={{ padding: 8, borderRadius: 99, border: '1px solid var(--line-2)', background: 'var(--card)', cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
            <Icon name="chevL" size={18} />
          </button>
          <div style={{ flex: 1 }}><Bar value={((i + 1) / view.length) * 100} /></div>
          <span className="u-mono" style={{ fontSize: 13, color: 'var(--ink-3)' }}>{i + 1}/{view.length}</span>
        </div>
      </div>

      <div style={{ maxWidth: 640, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20, padding: '20px 20px 28px' }}>
        <Badge kind="line">{q.type === 'single' ? 'Single choice' : q.type === 'multiple' ? 'Select all' : 'True / False'}</Badge>
        <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0, lineHeight: 1.4, letterSpacing: '-.01em' }}>{q.stem}</h2>
        {q.imageUrl && <img src={q.imageUrl} alt="" style={{ width: '100%', borderRadius: 12, border: '1px solid var(--line)', display: 'block' }} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {q.options.map((opt, oi) => {
            const on = multi ? (Array.isArray(a) ? a.includes(oi) : false) : a === oi;
            return (
              <button key={oi} onClick={() => pick(q.id, oi, multi)} style={{ all: 'unset', cursor: 'pointer', display: 'block' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '15px 16px', borderRadius: 12, border: `1.5px solid ${on ? 'var(--sage)' : 'var(--line-2)'}`, background: on ? 'var(--sage-tint)' : 'var(--card)', transition: 'all .14s' }}>
                  <div style={{ width: 22, height: 22, flex: 'none', borderRadius: multi ? 6 : '50%', border: `1.6px solid ${on ? 'var(--sage)' : 'var(--line-2)'}`, background: on ? 'var(--sage)' : 'transparent', display: 'grid', placeItems: 'center', color: 'var(--on-accent)' }}>
                    {on && <Icon name="check" size={13} stroke={3} />}
                  </div>
                  <span style={{ fontSize: 15.5, fontWeight: on ? 500 : 400 }}>{opt}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ position: 'sticky', bottom: 0, padding: '14px 20px 28px', background: 'linear-gradient(180deg, transparent, var(--paper) 28%)' }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <button
            className="btn btn-primary btn-lg btn-block"
            disabled={!answered || submitTest.isPending}
            onClick={() => (last ? submit() : setI(i + 1))}
          >
            {last ? (submitTest.isPending ? 'Submitting…' : 'Submit test') : 'Next question'} <Icon name="arrowR" size={17} />
          </button>
        </div>
      </div>
    </div>
  );
}
